/**
 * ReportsPage — list saved reports + a compact "New from chat" wizard
 * that drives a linear useConversationalFlow through name → event →
 * describe → preview → save. The wizard calls /api/reports/from-chat
 * for the NL→spec step and POST /api/reports on confirm.
 *
 * ReportDetailPage is the sibling route that actually renders a saved
 * report (viewer + editor toggle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3, Plus, Sparkles } from 'lucide-react'

import { PageLayout } from '../components/PageLayout'
import { useConversationalFlow, type Step } from '@/hooks/useConversationalFlow'
import { useAuth } from '@/contexts/AuthContext'
import type { ReportFull, ReportSpec, ReportSummary } from '../components/reports/types'
import { TemplatesTab } from '../components/reports/templates/TemplatesTab'

export { ReportDetailPage } from './ReportDetailPage'

type WizardStepId = 'name' | 'event' | 'describe' | 'confirm'

const WIZARD_STEPS: ReadonlyArray<Step<WizardStepId, string>> = [
  { id: 'name', label: 'Name this report', validate: (v) => typeof v === 'string' && v.trim().length >= 3 },
  { id: 'event', label: 'Which event?' },
  { id: 'describe', label: 'What would you like to see?', validate: (v) => typeof v === 'string' && v.trim().length >= 5 },
  { id: 'confirm', label: 'Preview and save' },
]

type TabId = 'saved' | 'templates'

export function ReportsPage() {
  const navigate = useNavigate()
  const { events, currentEventId } = useAuth()
  const [reports, setReports] = useState<ReportSummary[]>([])
  const [listLoading, setListLoading] = useState<boolean>(true)
  const [showWizard, setShowWizard] = useState<boolean>(false)
  const [tab, setTab] = useState<TabId>('saved')

  useEffect(() => {
    let cancelled = false
    setListLoading(true)
    fetch('/api/reports', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : { reports: [] }))
      .then((data: { reports?: ReportSummary[] }) => {
        if (cancelled) return
        setReports(Array.isArray(data?.reports) ? data.reports : [])
      })
      .finally(() => {
        if (!cancelled) setListLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const refreshList = useCallback(async () => {
    const res = await fetch('/api/reports', { credentials: 'same-origin' })
    if (res.ok) {
      const data = (await res.json()) as { reports?: ReportSummary[] }
      setReports(Array.isArray(data.reports) ? data.reports : [])
    }
  }, [])

  async function createBlank() {
    const now = Date.now()
    const slug = `new-report-${now.toString(36)}`
    const blankSpec: ReportSpec = {
      schema_version: 1,
      name: 'New report',
      event_scope: { event_ids: [], include_all: true },
      date_range: { relative: 'last_30d' },
      widgets: [
        {
          id: `kpi_${now.toString(36)}`,
          type: 'kpi',
          title: 'Total revenue',
          metric: 'revenue_cents',
          layout: { x: 0, y: 0, w: 3, h: 2 },
        },
      ],
    }
    const res = await fetch('/api/reports', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, name: 'New report', spec: blankSpec }),
    })
    if (res.ok) {
      const data = (await res.json()) as { report?: ReportFull }
      if (data.report) navigate(`/reports/${data.report.id}`)
    }
  }

  return (
    <PageLayout
      title="Reports"
      subtitle="Custom dashboards from a chat prompt or the form builder"
      actions={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700"
            data-testid="new-from-chat"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            New from chat
          </button>
          <button
            type="button"
            onClick={createBlank}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            data-testid="new-blank"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Blank report
          </button>
        </div>
      }
    >
      {showWizard && (
        <NewReportWizard
          events={events}
          onClose={() => setShowWizard(false)}
          onCreated={(id) => {
            setShowWizard(false)
            void refreshList()
            navigate(`/reports/${id}`)
          }}
        />
      )}

      <div
        role="tablist"
        aria-label="Reports view"
        className="mb-4 inline-flex rounded-md border border-slate-200 bg-white p-1 text-xs font-semibold dark:border-slate-700 dark:bg-slate-800"
        data-testid="reports-tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'saved'}
          onClick={() => setTab('saved')}
          className={
            tab === 'saved'
              ? 'rounded bg-butler-600 px-3 py-1 text-white'
              : 'px-3 py-1 text-slate-600 hover:text-butler-700 dark:text-slate-300'
          }
          data-testid="reports-tab-saved"
        >
          Saved
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'templates'}
          onClick={() => setTab('templates')}
          className={
            tab === 'templates'
              ? 'rounded bg-butler-600 px-3 py-1 text-white'
              : 'px-3 py-1 text-slate-600 hover:text-butler-700 dark:text-slate-300'
          }
          data-testid="reports-tab-templates"
        >
          Templates
        </button>
      </div>

      {tab === 'templates' ? (
        <TemplatesTab events={events} currentEventId={currentEventId} />
      ) : listLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800">
          Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-panel border border-dashed border-butler-200 bg-gradient-to-br from-white via-butler-50/60 to-midway-50/40 p-10 text-center shadow-card-soft dark:border-butler-800 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-card-soft ring-1 ring-butler-200 dark:bg-slate-900 dark:ring-butler-700"
            aria-hidden="true"
          >
            <BarChart3 className="h-6 w-6 text-butler-600 dark:text-butler-200" />
          </div>
          <h2 className="font-display text-lg font-semibold text-butler-800 dark:text-butler-100">
            Nothing saved yet
          </h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-ink-soft dark:text-slate-400">
            Describe a dashboard in plain English — e.g. <em>&ldquo;revenue by day with guest count overlay&rdquo;</em> — or start from a blank canvas.
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700"
              data-testid="empty-new-from-chat"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              New from chat
            </button>
            <button
              type="button"
              onClick={createBlank}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-surface-hover dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              data-testid="empty-new-blank"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Blank canvas
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {reports.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => navigate(`/reports/${r.id}`)}
              className="group rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-butler-500 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-butler-600 dark:text-butler-300">
                <BarChart3 className="h-4 w-4" aria-hidden="true" />
                Report
              </div>
              <div className="mt-2 truncate text-sm font-semibold text-slate-800 group-hover:text-butler-700 dark:text-slate-100">
                {r.name}
              </div>
              {r.description && (
                <div className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                  {r.description}
                </div>
              )}
              <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-400">
                Updated {new Date(r.updated_at).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}
              </div>
            </button>
          ))}
        </div>
      )}
    </PageLayout>
  )
}

interface NewReportWizardProps {
  events: Array<{ EventID: number; EventName: string }>
  onClose: () => void
  onCreated: (id: number) => void
}

function NewReportWizard({ events, onClose, onCreated }: NewReportWizardProps) {
  const flow = useConversationalFlow<WizardStepId, string>({ steps: WIZARD_STEPS })
  const [draftValue, setDraftValue] = useState<string>('')
  const [translating, setTranslating] = useState<boolean>(false)
  const [preview, setPreview] = useState<ReportSpec | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const stepId = flow.currentStep?.id ?? 'done'
  const canAdvance = useMemo(() => {
    if (stepId === 'event') return true // optional step — user can skip
    if (stepId === 'describe') return draftValue.trim().length >= 5
    return draftValue.trim().length > 0
  }, [draftValue, stepId])

  async function advance() {
    if (stepId === 'describe') {
      setTranslating(true)
      setError(null)
      setRefusal(null)
      try {
        const res = await fetch('/api/reports/from-chat', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            utterance: draftValue,
            events: events.slice(0, 20),
          }),
        })
        const body = await res.json()
        if (!res.ok) {
          setError(body?.message || 'translator unavailable')
          setTranslating(false)
          return
        }
        if (body.refusal) {
          setRefusal(body.refusal)
          setTranslating(false)
          return
        }
        setPreview(body.spec as ReportSpec)
        flow.advance(draftValue)
        setDraftValue('')
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        setTranslating(false)
      }
      return
    }
    flow.advance(draftValue)
    setDraftValue('')
  }

  async function save() {
    if (!preview) return
    setSaving(true)
    const name = flow.state.name ?? preview.name
    const slug = `r-${Date.now().toString(36)}`
    const finalSpec: ReportSpec = { ...preview, name }
    try {
      const res = await fetch('/api/reports', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, name, spec: finalSpec }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setError(body?.message || `save failed (${res.status})`)
        setSaving(false)
        return
      }
      const body = (await res.json()) as { report?: ReportFull }
      if (body.report) onCreated(body.report.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="new-report-wizard">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-butler-600 dark:text-butler-300">
            {flow.currentStep?.label ?? 'Review'}
          </h2>
          <button type="button" onClick={onClose} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
        </div>

        {stepId === 'name' && (
          <input
            autoFocus
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            placeholder="e.g. revenue mix by plan type"
            className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        )}

        {stepId === 'event' && (
          <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <p className="text-xs text-slate-500">Optional — pick a single event to scope, or leave blank for all events.</p>
            <select
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="">All events</option>
              {events.map((e) => (
                <option key={e.EventID} value={String(e.EventID)}>{e.EventName}</option>
              ))}
            </select>
          </div>
        )}

        {stepId === 'describe' && (
          <textarea
            rows={3}
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            placeholder="e.g. Revenue by day for the current event with guest count overlay"
            className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        )}

        {stepId === 'confirm' && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900">
            {preview ? (
              <>
                <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">Preview</div>
                <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                  {preview.widgets.length} widget{preview.widgets.length !== 1 ? 's' : ''}:{' '}
                  {preview.widgets.map((w) => w.type).join(', ')}
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-500">No preview available yet.</div>
            )}
          </div>
        )}

        {refusal && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            {refusal}
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            disabled={flow.currentIndex === 0}
            onClick={() => flow.goBack()}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200"
          >
            Back
          </button>
          {stepId === 'confirm' ? (
            <button
              type="button"
              disabled={!preview || saving}
              onClick={save}
              className="rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700 disabled:opacity-50"
              data-testid="wizard-save"
            >
              {saving ? 'Saving…' : 'Save report'}
            </button>
          ) : (
            <button
              type="button"
              disabled={!canAdvance || translating}
              onClick={advance}
              className="rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700 disabled:opacity-50"
              data-testid="wizard-advance"
            >
              {translating ? 'Thinking…' : 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
